import { useEffect, useRef, useLayoutEffect } from "react";

const SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function loadScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector(`script[src="${SRC}"]`);
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = SRC; s.async = true; s.defer = true;
    s.onload = resolve; s.onerror = reject;
    document.body.appendChild(s);
  });
}

// Invisible/managed Cloudflare Turnstile widget. Calls onToken(token) when solved.
export default function Turnstile({ onToken, onExpire, theme = "light" }) {
  const elRef = useRef(null);
  const idRef = useRef(null);
  // Keep latest callbacks in refs so changing identities (inline arrows in
  // parents) never remount the widget — remounting resets verification.
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  useLayoutEffect(() => { onTokenRef.current = onToken; onExpireRef.current = onExpire; });

  useEffect(() => {
    let cancelled = false;
    if (!SITE_KEY) return;
    loadScript().then(() => {
      if (cancelled || !elRef.current || !window.turnstile) return;
      idRef.current = window.turnstile.render(elRef.current, {
        sitekey: SITE_KEY,
        theme,
        callback: (token) => onTokenRef.current?.(token),
        "expired-callback": () => {
          onExpireRef.current?.();
          if (idRef.current != null) window.turnstile.reset(idRef.current);
        },
        "error-callback": () => onExpireRef.current?.(),
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (idRef.current != null && window.turnstile) {
        try { window.turnstile.remove(idRef.current); } catch { /* noop */ }
      }
    };
  }, [theme]);

  if (!SITE_KEY) return null;
  return <div ref={elRef} data-testid="turnstile-widget" className="mt-1" />;
}
